/**
 * Stock Transfers — list and composer pages for inter-warehouse moves.
 *
 * `StockTransfersPage` — paginated list of all transfers, newest first.
 * `StockTransferComposerPage` — form to execute a new transfer.
 *
 * @see packages/api-contract/src/procedures/stock.ts — transfersListRow / transferInput
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowLeftRight, ArrowRight, Plus } from "lucide-react";
import type { stock as stockContract } from "@flint/api-contract";
import type { z } from "zod";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { ListPagination, avatarCssColorFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";
import "./stock-transfers-page.css";

type TransfersListRow = z.infer<typeof stockContract.transfersListRow>;

const PAGE_SIZE = 50;

// ── List page ─────────────────────────────────────────────────────

export function StockTransfersPage() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  const list = trpc.stock.transfersList.useQuery(
    {
      limit: PAGE_SIZE,
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const rows = (list.data?.items as TransfersListRow[] | undefined) ?? [];
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;

  return (
    <div className="px-6 pt-5 pb-16 max-w-[1320px] mx-auto w-full space-y-5">
      {/* ── Page head ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">
            Stock transfers
          </h1>
          <p className="mt-1 text-sm text-muted">Inter-warehouse stock movements at WAC rate</p>
        </div>
        <Link
          to="/inventory/stock-transfers/new"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 shrink-0"
        >
          <Plus size={13} aria-hidden />
          New transfer
        </Link>
      </header>

      {/* ── Body ──────────────────────────────────────────────────── */}
      {list.isLoading ? (
        <p className="text-sm text-muted py-4">Loading transfers…</p>
      ) : list.error ? (
        <div className="rounded-md border border-line p-8 text-center bg-paper">
          <p className="text-sm font-semibold text-ink-2">Couldn't load transfers</p>
          <p className="text-xs text-muted mt-1">{list.error.message}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-line p-12 text-center bg-paper">
          <ArrowLeftRight size={32} className="mx-auto text-muted/40 mb-3" aria-hidden />
          <h3 className="text-sm font-semibold text-ink-2">No transfers yet</h3>
          <p className="text-xs text-muted mt-1 max-w-xs mx-auto">
            Move stock between warehouses to keep your location balances accurate.
          </p>
          <Link
            to="/inventory/stock-transfers/new"
            className="mt-4 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-ink text-paper text-xs font-semibold hover:bg-ink-2"
          >
            <Plus size={12} aria-hidden /> New transfer
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed text-sm" aria-label="Stock transfers">
              <colgroup>
                <col style={{ width: 110 }} />
                <col style={{ width: 200 }} />
                <col />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <thead className="bg-raised text-xs font-medium text-muted border-b border-line">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-3 py-3 text-left">From → To</th>
                  <th className="px-3 py-3 text-right">Qty</th>
                  <th className="px-3 py-3 text-right">Value</th>
                  <th className="px-3 py-3 text-left hidden xl:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <TransferTableRow key={row.referenceId} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-line">
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={showingTo + (list.data?.nextCursor ? 1 : 0)}
              sortLabel="newest first"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={() => setCursorStack((s) => s.slice(0, -1))}
              onNext={() => {
                const next = list.data?.nextCursor;
                if (next) setCursorStack((s) => [...s, next]);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TransferTableRow({ row }: { row: TransfersListRow }) {
  return (
    <tr className="border-t border-line hover:bg-raised transition-colors">
      {/* Date */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-muted tabular-nums whitespace-nowrap">
          {formatDate(row.createdAt)}
        </span>
      </td>

      {/* Item — avatar + name + SKU */}
      <td className="px-4 py-3">
        <Link
          to="/sales/items/$itemId"
          params={{ itemId: row.itemId }}
          className="flex items-center gap-2.5 min-w-0 group"
        >
          <span
            aria-hidden
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white"
            style={{ background: avatarCssColorFor(row.itemName) }}
          >
            {initialsFor(row.itemName)}
          </span>
          <div className="min-w-0">
            <span className="block truncate font-semibold text-ink text-sm leading-tight group-hover:text-sky-600">
              {row.itemName}
            </span>
            {row.itemSku ? (
              <span className="block truncate font-mono text-xs text-muted">{row.itemSku}</span>
            ) : null}
          </div>
        </Link>
      </td>

      {/* From → To warehouse */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-ink-2 truncate">{row.fromWarehouseName}</span>
          <ArrowRight size={10} className="text-muted shrink-0" aria-hidden />
          <span className="text-xs font-semibold text-ink truncate">{row.toWarehouseName}</span>
        </div>
      </td>

      {/* Qty */}
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <span className="font-mono font-semibold text-sm text-ink tabular-nums">
          {row.quantity.toLocaleString("en-IN")}
        </span>
        <span className="font-mono text-xs text-muted ml-0.5">{row.itemUnit}</span>
      </td>

      {/* Value */}
      <td className="px-3 py-3">
        <span className="font-mono text-sm font-semibold text-ink tabular-nums block text-right">
          {paiseToInrShort(row.amountPaise)}
        </span>
      </td>

      {/* Notes */}
      <td className="px-3 py-3 hidden xl:table-cell">
        <span className="text-xs text-muted truncate block w-full">{row.notes ?? "—"}</span>
      </td>
    </tr>
  );
}

// ── Composer page ─────────────────────────────────────────────────

export function StockTransferComposerPage() {
  const navigate = useNavigate();

  const [itemId, setItemId] = useState("");
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const warehouseList = trpc.warehouses.list.useQuery({ includeInactive: false });
  const warehouses = warehouseList.data?.warehouses ?? [];

  const stockSummary = trpc.stock.summary.useQuery({ limit: 100 });
  const balances = stockSummary.data?.items ?? [];

  const itemOptions = Array.from(
    new Map(balances.map((b) => [b.itemId, { id: b.itemId, name: b.itemName, sku: b.itemSku }])).values(),
  );

  const selectedBalance = balances.find(
    (b) => b.itemId === itemId && b.warehouseId === fromWarehouseId,
  );

  const utils = trpc.useUtils();
  const transferMutation = trpc.stock.transfer.useMutation({
    onSuccess: () => {
      // Refresh the transfers list + stock summary so the new transfer and
      // its balance impact (both source + destination warehouses) show up
      // immediately on next mount instead of after a manual refresh.
      void utils.stock.invalidate();
      void navigate({ to: "/inventory/stock-transfers" });
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const qty = parseInt(quantity, 10);
    if (!itemId || !fromWarehouseId || !toWarehouseId || isNaN(qty) || qty <= 0) {
      setError("Fill all required fields with valid values.");
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      setError("Source and destination warehouses must be different.");
      return;
    }

    transferMutation.mutate({
      itemId,
      fromWarehouseId,
      toWarehouseId,
      quantity: qty,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  }

  return (
    <div className="stock-transfer-composer">
      {/* ── Header — pinned, never scrolls ───────────────────────── */}
      <header className="stc-head">
        <Link to="/inventory/stock-transfers" className="stc-head-back" aria-label="Back to transfers">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="stc-head-icon" aria-hidden>
          <ArrowLeftRight size={16} />
        </div>
        <div className="stc-head-text">
          <h1>Transfer stock</h1>
        </div>
        <span className="stc-status-chip">New</span>
      </header>

      {/* ── Scrollable form body ──────────────────────────────────── */}
      {/* `.stc-body` is the full-width scroller (scrollbar at viewport right
          edge); `.stc-body-inner` is the 760px centered content column. */}
      <form id="stc-form" className="stc-body" onSubmit={handleSubmit}>
        <div className="stc-body-inner">
        <div className="stc-section">
          <div className="stc-section-head">
            <span className="stc-step">1</span>
            <div>
              <h3>Transfer details</h3>
              <span className="stc-why">Move stock between warehouses at the current WAC rate.</span>
            </div>
          </div>
          <div className="stc-section-body">
            {/* Item */}
            <div className="stc-field">
              <label>
                Item <span className="stc-req">*</span>
              </label>
              {/* Picker state — empty dropdown means the workspace has no
                  stock balances yet (opening-balance / adjustment hasn't been
                  recorded). One message drives both the trigger placeholder
                  and the inline hint so they stay in sync. */}
              {(() => {
                const emptyMessage = stockSummary.isLoading ? "Loading stock…" : "No stock found";
                const hasItems = itemOptions.length > 0;
                return (
                  <Select value={itemId} onValueChange={setItemId}>
                    <SelectTrigger className="!h-9 !text-sm">
                      <SelectValue placeholder={hasItems ? "Select item…" : emptyMessage} />
                    </SelectTrigger>
                    <SelectContent>
                      {hasItems ? (
                        itemOptions.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.sku ? `${item.sku} — ` : ""}{item.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-xs text-muted">{emptyMessage}</div>
                      )}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>

            {/* From → To warehouses */}
            <div className="stc-transfer-row">
              <div className="stc-field">
                <label>
                  From warehouse <span className="stc-req">*</span>
                </label>
                <Select value={fromWarehouseId} onValueChange={setFromWarehouseId}>
                  <SelectTrigger className="!h-9 !text-sm">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((wh) => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.name}{wh.isDefault ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBalance ? (
                  <span className="stc-hint-ok">
                    Available: {selectedBalance.quantityOnHand.toLocaleString("en-IN")}
                    {" · "}
                    {paiseToInrShort(BigInt(selectedBalance.totalValuePaise))}
                  </span>
                ) : null}
              </div>

              <div className="stc-transfer-arrow" aria-hidden>
                <ArrowRight size={16} />
              </div>

              <div className="stc-field">
                <label>
                  To warehouse <span className="stc-req">*</span>
                </label>
                <Select value={toWarehouseId} onValueChange={setToWarehouseId}>
                  <SelectTrigger className="!h-9 !text-sm">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses
                      .filter((wh) => wh.id !== fromWarehouseId)
                      .map((wh) => (
                        <SelectItem key={wh.id} value={wh.id}>
                          {wh.name}{wh.isDefault ? " (default)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quantity */}
            <div className="stc-field">
              <label>
                Quantity <span className="stc-req">*</span>
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className="stc-num"
              />
              {selectedBalance && quantity && !isNaN(parseInt(quantity, 10)) &&
                parseInt(quantity, 10) > selectedBalance.quantityOnHand ? (
                  <span className="stc-hint-bad">
                    Exceeds available stock ({selectedBalance.quantityOnHand.toLocaleString("en-IN")})
                  </span>
                ) : null}
            </div>

            {/* Notes */}
            <div className="stc-field">
              <label>
                Notes <span className="stc-opt">optional</span>
              </label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for transfer (optional)"
              />
            </div>

            {error && <Alert intent="bad" description={error} />}
          </div>
        </div>
        </div>
      </form>

      {/* ── Footer — pinned, never scrolls ───────────────────────── */}
      <footer className="stc-foot">
        <div className="stc-foot-actions">
          <button
            type="submit"
            form="stc-form"
            disabled={transferMutation.isPending}
            className="inline-flex items-center h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {transferMutation.isPending ? "Transferring…" : "Transfer stock"}
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: "/inventory/stock-transfers" })}
            className="inline-flex items-center h-9 px-4 rounded-md border border-line bg-paper text-sm font-semibold text-ink-2 hover:bg-bg"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  );
}
